<!-- THE FIRST RUN. Five screens, each one the app's own drawing doing the thing
     it promises — not a screenshot of it, and not an illustration of it.

     Why not the chassis's Onboarding: it takes a title, a line and a picture,
     and this app's whole case is a moving bar. So the cards here are built from
     the same parts the app is: the Lifts row with its dashed number, the ramp as
     the warm-ups list prints it, RampSet loading the bar set by set with the
     lifter's setting for motion, Kit's plates in the kit's own colours, and a
     Timeline row. Every number on them comes from buildRamp against the kit on
     this phone, so a lifter in pounds sees pounds.

     The progress is the ramp's own segment bar, because that is what working up
     looks like in this app. One primary action at the bottom, Back beside it as
     on the ramp, Skip at the top. Swipe works too; the buttons are the way. -->
<template>
  <IonModal
    :is-open="firstRunOpen" :backdrop-dismiss="false" class="first-run" aria-label="Welcome to Work Up"
    @did-dismiss="closeFirstRun"
  >
    <IonContent :scroll-y="false">
      <div class="fr" @touchstart.passive="touchStart" @touchend.passive="touchEnd">
        <div class="fr-top">
          <div class="segs" aria-hidden="true">
            <span
              v-for="(c, i) in CARDS" :key="c.id"
              class="seg" :class="{ done: i < index, current: i === index }"
            />
          </div>
          <IonButton v-if="!last" fill="clear" size="small" class="skip" @click="finish()">Skip</IonButton>
        </div>

        <Transition :name="dir" mode="out-in">
          <div :key="card.id" class="fr-card">
            <div class="fr-art" aria-hidden="true">
              <template v-if="ready">
                <!-- 1 · LIFTS: the top set, typed. -->
                <div v-if="card.id === 'top'" class="a-lifts">
                  <div v-for="(l, i) in liftRows" :key="l.name" class="a-lift" :class="{ quiet: i > 0 }">
                    <div class="a-lift-head">
                      <span class="serif a-name">{{ l.name }}</span>
                      <span class="a-top">
                        <span class="serif a-num">{{ i === 0 ? typed : l.top }}<span v-if="i === 0 && typing" class="caret" /></span>
                        <span class="a-per">{{ unit }} × 1</span>
                      </span>
                    </div>
                    <p class="a-meta">
                      <span class="fade" :class="{ show: i > 0 || !typing }">{{ l.sets }}</span>
                    </p>
                  </div>
                </div>

                <!-- 2 · WARM-UPS: the plan, a row at a time. -->
                <ol v-else-if="card.id === 'plan'" class="a-plan">
                  <li v-for="(s, i) in squat" :key="i" class="a-row" :style="{ animationDelay: `${120 + i * 70}ms` }">
                    <span class="serif a-w">{{ fmt(s.total) }}<small>{{ unit }}</small></span>
                    <span class="a-r">× {{ perSet(s) }}</span>
                    <span class="a-p">{{ s.stack.length ? s.stack.map(fmt).join(' · ') : 'empty bar' }}</span>
                  </li>
                </ol>

                <!-- 3 · BETWEEN SETS: the ramp itself, loading set after set. -->
                <div v-else-if="card.id === 'change'" class="a-change">
                  <RampSet :set="squat[step]" state="current" :kit="kit" :plates="kitPlates" :animate="loadingMotion" />
                </div>

                <!-- 4 · KIT: every plate you own, in its colour. -->
                <div v-else-if="card.id === 'kit'" class="a-kit">
                  <BarSleeve
                    class="a-kit-bar" compact
                    :stack="kitSample" :plates="kitPlates" :unit="unit"
                    :collar-weight="Number(kit.collarWeight) || 0"
                  />
                  <div class="a-tiles">
                    <span v-for="(p, i) in kitPlates.slice(0, 9)" :key="p.id ?? p.weight" class="a-tile" :style="{ animationDelay: `${100 + i * 50}ms` }">
                      <span class="dot" :style="{ background: p.colour }" :class="{ pale: isPale(p.colour), rim: isDark(p.colour) }" />
                      {{ fmt(Number(p.weight)) }}
                    </span>
                  </div>
                </div>

                <!-- 5 · TIMELINE: sessions, a row per lift per day. -->
                <div v-else-if="card.id === 'log'" class="a-log">
                  <div v-for="(d, i) in logRows" :key="d.name" class="a-day" :style="{ animationDelay: `${100 + i * 90}ms` }">
                    <p class="a-date">{{ d.date }}</p>
                    <div class="a-entry">
                      <span class="serif a-name">{{ d.name }}</span>
                      <span class="a-best"><b>{{ d.top }}</b> {{ unit }} × 1</span>
                    </div>
                    <p class="a-meta show">{{ d.sets }}<template v-if="d.filmed"> · <span class="filmed">filmed</span></template></p>
                  </div>
                </div>
              </template>
            </div>

            <div class="fr-say">
              <p class="wu-label">{{ card.where }}</p>
              <h1 class="serif fr-title">{{ card.title }}</h1>
              <p class="fr-body">{{ card.body }}</p>
            </div>
          </div>
        </Transition>
        <p class="sr-only" aria-live="polite">Step {{ index + 1 }} of {{ CARDS.length }}</p>

        <div class="fr-go">
          <!-- Always there, disabled on the first card, so Next never changes
               width under the finger (see RampPage's Back). -->
          <button
            type="button" class="wu-btn-quiet back" :disabled="index === 0"
            aria-label="Back" @click="go(-1)"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 6l-6 6 6 6" /></svg>
          </button>
          <button type="button" class="wu-btn next" @click="last ? finish() : go(1)">
            {{ last ? 'Enter my top sets' : 'Next' }}
          </button>
        </div>
        <div class="fr-after">
          <button v-if="last" type="button" class="wu-link" @click="finish('kit')">Set up my kit first</button>
        </div>
      </div>
    </IonContent>
  </IonModal>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonModal, IonContent, IonButton } from '@ionic/vue';
import BarSleeve from './BarSleeve.vue';
import RampSet from './RampSet.vue';
import { buildRamp, fmt, rangeTo } from '../lib/loading.js';
import { DEFAULT_STEPS, currentKit, platesFor } from '../lib/seed.js';
import { loadingMotion, reducedMotion } from '../lib/prefs.js';
import { firstRunOpen, openFirstRunIfNew, closeFirstRun } from '../lib/firstRun.js';

const router = useRouter();

const CARDS = [
  { id: 'top', where: 'Lifts', title: 'Type your top set.', body: 'One number for each lift. Work Up plans every warm-up up to it.' },
  { id: 'plan', where: 'Warm-ups', title: 'Warm-ups, worked out.', body: 'The empty bar, five warm-ups and your top set, each with its reps and the plates on each side. Change the formula per lift, or just for today.' },
  { id: 'change', where: 'Between sets', title: 'Only what changes.', body: 'It shows what to take off and what to add, each side. Never the whole bar again.' },
  { id: 'kit', where: 'Kit', title: 'Your plates, your colours.', body: 'Tell it your bar, collars and the plates your gym has, in kg or lb. It never plans a loading you can’t make.' },
  { id: 'log', where: 'Timeline', title: 'Every set, logged.', body: 'Next set records it. Film the set that matters, export any stretch of history, back it all up to one file.' },
];

const index = ref(0);
const dir = ref('fr-fwd');
const card = computed(() => CARDS[index.value]);
const last = computed(() => index.value === CARDS.length - 1);

function go(by) {
  const to = index.value + by;
  if (to < 0 || to >= CARDS.length) return;
  dir.value = by > 0 ? 'fr-fwd' : 'fr-back';
  index.value = to;
}

function finish(where) {
  closeFirstRun();
  index.value = 0;
  if (where === 'kit') router.push('/tabs/kit');
}

// Swipe, as a convenience beside the buttons. Horizontal and deliberate only.
let x0 = null; let y0 = null;
const touchStart = (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; };
function touchEnd(e) {
  if (x0 == null) return;
  const dx = e.changedTouches[0].clientX - x0;
  const dy = e.changedTouches[0].clientY - y0;
  x0 = null;
  if (Math.abs(dx) > 56 && Math.abs(dx) > 1.5 * Math.abs(dy)) go(dx < 0 ? 1 : -1);
}

// ---- the numbers, from this phone's kit -------------------------------------
const kit = computed(() => currentKit());
const kitPlates = computed(() => (kit.value ? platesFor(kit.value) : []));
const unit = computed(() => kit.value?.unit || 'kg');
const lb = computed(() => unit.value === 'lb');
const STEPS = DEFAULT_STEPS.map((s, position) => ({ ...s, position }));

function ramp(top, start, startReps, startRepsTo) {
  if (!kit.value || !kitPlates.value.length) return null;
  const r = buildRamp({ topSet: top, topSetReps: 1, startWeight: start, startReps, startRepsTo }, STEPS, kit.value, kitPlates.value);
  return r.ok ? r.sets : null;
}
const bar = computed(() => Number(kit.value?.barWeight) || 20);
const pull = computed(() => bar.value + 2 * (Number(kit.value?.collarWeight) || 0) + (lb.value ? 90 : 40));
const tops = computed(() => (lb.value ? { squat: 405, bench: 275, deadlift: 495 } : { squat: 180, bench: 125, deadlift: 220 }));
const squat = computed(() => ramp(tops.value.squat, bar.value, 6, 10));
const bench = computed(() => ramp(tops.value.bench, bar.value, 6, 10));
const dead = computed(() => ramp(tops.value.deadlift, pull.value, 5, null));
const ready = computed(() => !!(squat.value && bench.value && dead.value));

const setsLine = (sets) => `${sets.length} sets · ${fmt(sets[0].total)} → ${fmt(sets[sets.length - 1].total)}`;
const liftRows = computed(() => [
  { name: 'Squat', top: fmt(tops.value.squat), sets: setsLine(squat.value) },
  { name: 'Bench', top: fmt(tops.value.bench), sets: setsLine(bench.value) },
  { name: 'Deadlift', top: fmt(tops.value.deadlift), sets: setsLine(dead.value) },
]);

// As the Timeline prints a day: "Today · Fri 11 Sept", "Tue 8 Sept".
const day = (ago) => {
  const d = new Date(); d.setDate(d.getDate() - ago);
  const s = `${d.toLocaleDateString('en-AU', { weekday: 'short' })} ${d.getDate()} ${d.toLocaleDateString('en-AU', { month: 'short' })}`;
  return ago === 0 ? `Today · ${s}` : s;
};
const perSet = (s) => { const to = rangeTo(s.reps, s.repsTo); return to ? `${s.reps}–${to}` : String(s.reps); };
const logRows = computed(() => [
  { date: day(0), name: 'Squat', top: fmt(tops.value.squat), sets: setsLine(squat.value), filmed: true },
  { date: day(3), name: 'Deadlift', top: fmt(tops.value.deadlift), sets: setsLine(dead.value) },
  { date: day(4), name: 'Bench', top: fmt(tops.value.bench), sets: setsLine(bench.value) },
]);

const kitSample = computed(() => kitPlates.value.map((p) => Number(p.weight)));
// A white plate needs its edge on the light ground, a black one its rim on the
// dark — BarSleeve's rules.
const isPale = (hex) => /^#(f|e|d)/i.test(hex || '');
const isDark = (hex) => /^#[0-2][0-9a-f][0-2]/i.test(hex || '');

// ---- motion --------------------------------------------------------------------
// Card 1 types the top set; card 3 steps through the ramp. Both only while their
// card is showing, and card 3 stands still under Reduce Motion.
const typed = ref('');
const typing = ref(false);
const step = ref(1);
// Every timer this component starts, so one stop() ends all of them. A typing
// run that outlived its card used to keep calling stop() and killed the next
// card's loop.
let timers = [];
const stop = () => { timers.forEach((t) => clearTimeout(t)); timers = []; };
const later = (fn, ms) => { timers.push(setTimeout(fn, ms)); };

function play() {
  stop();
  if (!firstRunOpen.value || !ready.value) return;
  if (card.value.id === 'top') {
    const full = fmt(tops.value.squat);
    if (reducedMotion.value) { typed.value = full; typing.value = false; return; }
    typed.value = ''; typing.value = true;
    [...full].forEach((_, i) => later(() => { typed.value = full.slice(0, i + 1); }, 420 + i * 190));
    later(() => { typing.value = false; }, 420 + full.length * 190 + 450);
  } else if (card.value.id === 'change') {
    const n = squat.value.length;
    if (!loadingMotion.value) { step.value = Math.min(3, n - 1); return; }
    step.value = 1;
    const tick = () => { step.value = step.value >= n - 1 ? 1 : step.value + 1; later(tick, 2400); };
    later(tick, 2400);
  }
}

watch([index, firstRunOpen, ready], play);
onMounted(() => { openFirstRunIfNew(); });
onBeforeUnmount(stop);
</script>

<style scoped>
.fr {
  box-sizing: border-box;
  display: flex; flex-direction: column;
  height: 100%;
  padding: calc(var(--ion-safe-area-top, 0px) + 10px) var(--wu-pad) calc(var(--ion-safe-area-bottom, 0px) + 12px);
  background: var(--bg);
  color: var(--ink);
  overflow: hidden;
}

/* ---- progress: the ramp's segments ---- */
.fr-top { display: flex; align-items: center; gap: 12px; min-height: 44px; }
.segs { display: flex; gap: 4px; flex: 1 1 auto; }
.seg { flex: 1 1 0; height: 3px; border-radius: 2px; background: var(--line); transition: background-color 240ms ease, opacity 240ms ease; }
.seg.done { background: var(--ink); }
.seg.current { background: var(--ink); opacity: 0.45; }
.skip {
  --color: var(--dim); --padding-start: 10px; --padding-end: 0;
  margin: 0; min-height: 44px;
  font: 400 0.8125rem/1 var(--face-mono); letter-spacing: 0; text-transform: none;
}

.fr-card { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; }

/* ---- the art: centred in what is left, the bar allowed to reach the edge ---- */
.fr-art {
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column; justify-content: center;
  margin: 0 calc(-1 * var(--wu-pad)); padding: 8px var(--wu-pad) 0;
}

.fr-say { padding-top: 12px; min-height: 184px; }
.fr-say .wu-label { margin: 0 0 10px; }
.fr-title { margin: 0; font-size: 2.5rem; line-height: 1.02; letter-spacing: -0.01em; font-weight: 400; }
.fr-body { margin: 12px 0 0; max-width: 36ch; font: 400 0.875rem/1.6 var(--face-mono); color: var(--dim); }

.fr-go { display: flex; gap: 10px; margin-top: 18px; }
.fr-go .next { --wu-btn-h: 54px; flex: 1 1 auto; }
.fr-go .back { --wu-btn-h: 54px; width: 54px; padding: 0; flex: none; }
.fr-after { display: flex; justify-content: center; min-height: 44px; margin-top: 4px; }

/* ---- card transitions: a step along, and back ---- */
.fr-fwd-enter-active, .fr-fwd-leave-active,
.fr-back-enter-active, .fr-back-leave-active { transition: opacity 200ms ease, transform 240ms cubic-bezier(0.2, 0.7, 0.2, 1); }
.fr-fwd-enter-from, .fr-back-leave-to { opacity: 0; transform: translateX(28px); }
.fr-fwd-leave-to, .fr-back-enter-from { opacity: 0; transform: translateX(-28px); }

/* ---- 1 · lifts ---- */
.a-lifts { display: flex; flex-direction: column; }
.a-lift { padding: 18px 0 16px; border-top: 1px solid var(--line); }
.a-lift:first-child { border-top: 0; }
/* The other lifts step back in colour, not opacity: faded text would fail
   contrast, and a screen that fails it on its first card fails the app. */
.a-lift.quiet .a-name, .a-lift.quiet .a-num { color: var(--dim); }
.a-lift.quiet .a-num { border-bottom-color: var(--line); }
.a-lift-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.a-name { font-size: 1.875rem; line-height: 1; }
.a-top { display: inline-flex; align-items: baseline; gap: 8px; }
.a-num {
  position: relative; display: inline-block; min-width: 2.2ch; text-align: right;
  font-size: 3.25rem; line-height: 1; font-variant-numeric: tabular-nums;
  padding-bottom: 4px; border-bottom: 1.5px dashed var(--faint);
}
.caret { display: inline-block; width: 2px; height: 0.78em; margin-left: 3px; background: var(--ink); vertical-align: -0.02em; animation: blink 0.9s steps(1) infinite; }
@keyframes blink { 50% { opacity: 0; } }
.a-per { font: 400 0.875rem/1 var(--face-mono); color: var(--dim); }
.a-meta { margin: 10px 0 0; min-height: 1.2em; font: 400 0.8125rem/1.2 var(--face-mono); color: var(--dim); }
.a-meta .fade { opacity: 0; transition: opacity 300ms ease; }
.a-meta .fade.show { opacity: 1; }

/* ---- 2 · warm-ups ---- */
.a-plan { list-style: none; margin: 0; padding: 0; }
.a-row {
  display: grid; grid-template-columns: 4.8em 4.4em minmax(0, 1fr); align-items: baseline; gap: 10px;
  padding: 9px 0 8px; border-top: 1px solid var(--line);
  animation: rise 360ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
}
.a-row:first-child { border-top: 0; }
.a-w { font-size: 1.625rem; line-height: 1; font-variant-numeric: tabular-nums; }
.a-w small { font: 400 0.6875rem var(--face-mono); color: var(--dim); margin-left: 3px; }
.a-r, .a-p { font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); }
.a-p { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@keyframes rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* ---- 3 · between sets: RampSet as the ramp draws it ---- */
.a-change :deep(.now) { padding-top: 0; }

/* ---- 4 · kit ---- */
.a-kit-bar { --bleed: var(--wu-pad); --sleeve-span: min(62vw, 300px); }
.a-tiles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 18px; }
.a-tile {
  display: flex; align-items: center; gap: 10px; min-height: 42px; padding: 0 12px;
  border: 1px solid var(--field-line); border-radius: 12px; background: var(--field);
  font: 500 0.875rem/1 var(--face-mono); font-variant-numeric: tabular-nums;
  animation: rise 320ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
}
.dot { width: 14px; height: 14px; border-radius: 50%; flex: none; }
.dot.pale { box-shadow: inset 0 0 0 1px var(--pale-edge, #8A9197); }
.dot.rim { box-shadow: inset 0 0 0 1px var(--dark-rim, transparent); }

/* ---- 5 · timeline ---- */
.a-day { animation: rise 360ms cubic-bezier(0.2, 0.7, 0.2, 1) both; }
.a-day + .a-day { margin-top: 18px; }
.a-date {
  margin: 0; padding-bottom: 8px; border-bottom: 1px solid var(--line);
  font: 400 0.6875rem/1.4 var(--face-mono); letter-spacing: 0.1em; text-transform: uppercase; color: var(--dim);
}
.a-entry { display: flex; align-items: baseline; justify-content: space-between; margin-top: 12px; }
.a-best { font: 400 0.875rem/1 var(--face-mono); color: var(--dim); }
.a-best b { font-weight: 500; color: var(--ink); font-size: 1rem; }
.filmed { color: var(--ink); }

@media (prefers-reduced-motion: reduce) {
  .fr-fwd-enter-from, .fr-back-leave-to, .fr-fwd-leave-to, .fr-back-enter-from { transform: none; }
  .a-row, .a-tile, .a-day { animation: none; }
  .caret { animation: none; }
}

/* A short phone (SE): the art gives way before the words do. */
@media (max-height: 700px) {
  .fr-title { font-size: 2.125rem; }
  .fr-say { min-height: 164px; }
  .a-lift.quiet:last-child, .a-day:last-child { display: none; }
  .a-row { padding: 6px 0 5px; }
  .a-tiles { display: none; }
}
</style>

<style>
/* The modal's own ground, outside the scoped block because Ionic draws it. */
ion-modal.first-run { --background: var(--bg); }
ion-modal.first-run ion-content { --background: var(--bg); }
/* The web's "Install Work Up" toast would land on the first run's buttons.
   It waits until the walkthrough is closed. */
html:has(ion-modal.first-run:not(.overlay-hidden)) ion-toast { display: none; }
</style>
